import { useRef, useState } from 'react';

import {
  Plane,
  Upload,
  Send,
  CheckCircle2,
  FileWarning,
  Plus,
  AlertTriangle,
  Ticket,
} from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { post, patch, upload } from '../../api/client';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import EventPicker from '../../components/EventPicker';
import {
  Stat,
  Button,
  Modal,
  Field,
  Input,
  Select,
  Table,
  Badge,
  StatusBadge,
  Loader,
  EmptyState,
  SectionTitle,
  Tabs,
  SearchInput,
  ConfirmModal,
  PersonCell,
} from '../../components/UI';
import { fmtDateTime, pct } from '../../utils/format';
export default function Flights() {
  const { eventId, events } = useEvents();
  const toast = useToast();
  const [tab, setTab] = useState('readiness');
  const [search, setSearch] = useState('');
  const [askDocs, setAskDocs] = useState(false);

  const { data: readiness, loading, reload } = useFetch(
    '/flights/readiness', { event: eventId }, { skip: !eventId },
  );

  if (!events.length) {
    return <EmptyState message="No events available" hint="Travel management begins once you have an event." icon={Plane} />;
  }

  const s = readiness?.summary;
  const rows = (readiness?.rows || []).filter((r) =>
    !search || r.user.name.toLowerCase().includes(search.toLowerCase()));

  const filtered = tab === 'readiness' ? rows
    : tab === 'pending' ? rows.filter((r) => !r.ready)
    : rows.filter((r) => r.ready);

  const requestDocs = async () => {
    try {
      const res = await post('/flights/request-documents', { event: eventId });
      toast.success(res.message);
      setAskDocs(false);
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <>
      <SectionTitle
        title="Flights & travel"
        subtitle="Collect documents, issue tickets and monitor every journey"
        action={
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
            <EventPicker />
            <Button variant="ghost" icon={Send} onClick={() => setAskDocs(true)} disabled={!eventId}>
              Request documents
            </Button>
          </div>
        }
      />

      {s && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(195px, 1fr))', gap: 13, marginBottom: 18 }}>
          <Stat label="Travellers" value={s.total} icon={Plane} tone="info" />
          <Stat label="Docs Verified" value={s.docsVerified} icon={CheckCircle2} tone="success" progress={pct(s.docsVerified, s.total)} />
          <Stat label="Docs Pending" value={s.docsPending} icon={FileWarning} tone="warning" />
          <Stat label="Tickets Issued" value={s.ticketsIssued} icon={Ticket} tone="gold" progress={pct(s.ticketsIssued, s.total)} />
          <Stat label="Fully Ready" value={s.fullyReady} icon={CheckCircle2} tone="success" progress={pct(s.fullyReady, s.total)} />
        </div>
      )}

      <Tabs
        active={tab} onChange={setTab}
        tabs={[
          { key: 'readiness', label: 'All travellers', count: rows.length },
          { key: 'pending', label: 'Not ready', count: rows.filter((r) => !r.ready).length },
          { key: 'ready', label: 'Ready to fly', count: rows.filter((r) => r.ready).length },
        ]}
      />

      <SearchInput value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Search travellers…" style={{ maxWidth: 350, marginBottom: 16 }} />

      {loading ? <Loader label="Loading travel readiness" /> : (
        <ReadinessTable rows={filtered} eventId={eventId} onChange={reload} />
      )}

      <ConfirmModal
        open={askDocs} onClose={() => setAskDocs(false)} onConfirm={requestDocs}
        title="Request travel documents" confirmLabel="Send request"
        message="Everyone whose documents are still pending or were rejected will receive a WhatsApp, email and push reminder to upload their Aadhaar or passport."
      />
    </>
  );
}

function ReadinessTable({ rows, eventId, onChange }) {
  const toast = useToast();
  const [ticketFor, setTicketFor] = useState(null);
  const [flightFor, setFlightFor] = useState(null);

  const verify = async (userId, status) => {
    try {
      await patch(`/users/${userId}/documents/verify`, {
        status,
        reason: status === 'rejected' ? 'The uploaded document was not readable. Please upload a clear copy.' : undefined,
      });
      toast.success(status === 'verified' ? 'Documents verified' : 'Participant asked to re-upload');
      onChange();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <>
      <Table
        rows={rows}
        keyField="user"
        empty="No travellers match this view"
        columns={[
          { key: 'user', title: 'Traveller', render: (r) => <PersonCell person={r.user} /> },
          { key: 'documentStatus', title: 'Documents', render: (r) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <StatusBadge status={r.documentStatus} />
              {r.documentStatus === 'submitted' && (
                <div style={{ display: 'flex', gap: 5 }}>
                  <button onClick={(e) => { e.stopPropagation(); verify(r.user.id, 'verified'); }}
                    title="Approve" style={{ color: 'var(--ok)', display: 'flex', padding: 3 }}>
                    <CheckCircle2 size={15} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); verify(r.user.id, 'rejected'); }}
                    title="Ask for a re-upload" style={{ color: 'var(--danger)', display: 'flex', padding: 3 }}>
                    <AlertTriangle size={15} />
                  </button>
                </div>
              )}
            </div>
          )},
          { key: 'onward', title: 'Onward', render: (r) => <LegCell leg={r.onward} /> },
          { key: 'return', title: 'Return', render: (r) => <LegCell leg={r.return} /> },
          { key: 'ready', title: 'Status', render: (r) => (
            <Badge tone={r.ready ? 'success' : 'warning'} dot>{r.ready ? 'Ready' : 'Pending'}</Badge>
          )},
          { key: 'actions', title: '', align: 'right', render: (r) => (
            <div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
              <Button size="sm" variant="ghost" icon={Plus}
                onClick={(e) => { e.stopPropagation(); setFlightFor(r); }}>
                Flight
              </Button>
              {r.onward && (
                <Button size="sm" variant="outline" icon={Upload}
                  onClick={(e) => { e.stopPropagation(); setTicketFor(r.onward); }}>
                  Ticket
                </Button>
              )}
            </div>
          )},
        ]}
      />

      <TicketUploadModal flight={ticketFor} onClose={() => setTicketFor(null)} onDone={onChange} />
      <FlightFormModal row={flightFor} eventId={eventId} onClose={() => setFlightFor(null)} onDone={onChange} />
    </>
  );
}

function LegCell({ leg }) {
  if (!leg) return <span style={{ color: 'var(--text-lo)', fontSize: 12.5 }}>Not booked</span>;
  return (
    <div>
      <div style={{ fontSize: 12.5, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
        {leg.flightNumber || 'TBA'}
        {leg.hasTicket && <Ticket size={11} color="var(--ok)" />}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-lo)' }}>
        {leg.departureAt ? fmtDateTime(leg.departureAt) : 'Time TBA'}
      </div>
    </div>
  );
}

function TicketUploadModal({ flight, onClose, onDone }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!file) return toast.error('Attach the e-ticket PDF or image');
    setBusy(true);
    try {
      const res = await upload(`/flights/${flight.id}/ticket`, file);
      toast.success(res.message);
      setFile(null);
      onDone();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(flight)} onClose={onClose}
      title="Upload flight ticket"
      subtitle="The passenger is notified on WhatsApp, email and push the moment it lands"
      footer={
        <>
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button icon={Upload} onClick={send} loading={busy} disabled={!file}>Upload & notify</Button>
        </>
      }
    >
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files?.[0] || null); }}
        style={{
          padding: 32, textAlign: 'center', cursor: 'pointer',
          border: `1.5px dashed ${file ? 'var(--gold-500)' : 'var(--border)'}`,
          borderRadius: 'var(--r-lg)',
          background: file ? 'rgba(239,212,138,0.05)' : 'var(--ink-900)',
          transition: 'all 0.2s',
        }}
      >
        <input ref={fileRef} type="file" accept=".pdf,image/*" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <Ticket size={28} color={file ? 'var(--gold-400)' : 'var(--text-lo)'} style={{ marginBottom: 11 }} />
        <p style={{ fontSize: 13.5, fontWeight: 500 }}>{file ? file.name : 'Drop the e-ticket here, or click to browse'}</p>
        <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 6 }}>PDF or image, up to 10 MB</p>
      </div>
    </Modal>
  );
}

const FLIGHT_BLANK = {
  direction: 'onward', airline: '', flightNumber: '', pnr: '', seatNumber: '',
  cabinClass: 'economy', fromCode: '', fromCity: '', toCode: '', toCity: '',
  departureAt: '', arrivalAt: '',
};

function FlightFormModal({ row, eventId, onClose, onDone }) {
  const toast = useToast();
  const [form, setForm] = useState(FLIGHT_BLANK);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.flightNumber.trim()) return toast.error('Enter the flight number');
    setBusy(true);
    try {
      await post('/flights', {
        event: eventId,
        user: row.user.id,
        direction: form.direction,
        airline: form.airline,
        flightNumber: form.flightNumber.toUpperCase(),
        pnr: form.pnr.toUpperCase(),
        seatNumber: form.seatNumber,
        cabinClass: form.cabinClass,
        from: { code: form.fromCode.toUpperCase(), city: form.fromCity },
        to: { code: form.toCode.toUpperCase(), city: form.toCity },
        departureAt: form.departureAt || undefined,
        arrivalAt: form.arrivalAt || undefined,
        status: 'ticket_issued',
      });
      toast.success('Flight saved');
      setForm(FLIGHT_BLANK);
      onDone();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(row)} onClose={onClose} width={600}
      title="Add flight details" subtitle={row?.user?.name}
      footer={
        <>
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={busy}>Save flight</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
          <Field label="Direction">
            <Select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
              <option value="onward">Onward journey</option>
              <option value="return">Return journey</option>
            </Select>
          </Field>
          <Field label="Cabin class">
            <Select value={form.cabinClass} onChange={(e) => setForm({ ...form, cabinClass: e.target.value })}>
              <option value="economy">Economy</option>
              <option value="premium_economy">Premium Economy</option>
              <option value="business">Business</option>
              <option value="first">First</option>
            </Select>
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 0.9fr', gap: 11 }}>
          <Field label="Airline"><Input value={form.airline} onChange={(e) => setForm({ ...form, airline: e.target.value })} placeholder="IndiGo" /></Field>
          <Field label="Flight no." required><Input value={form.flightNumber} onChange={(e) => setForm({ ...form, flightNumber: e.target.value })} placeholder="6E2043" /></Field>
          <Field label="PNR"><Input value={form.pnr} onChange={(e) => setForm({ ...form, pnr: e.target.value })} placeholder="ABC123" /></Field>
          <Field label="Seat"><Input value={form.seatNumber} onChange={(e) => setForm({ ...form, seatNumber: e.target.value })} placeholder="12A" /></Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '0.8fr 1.4fr 0.8fr 1.4fr', gap: 11 }}>
          <Field label="From"><Input value={form.fromCode} onChange={(e) => setForm({ ...form, fromCode: e.target.value })} placeholder="BOM" maxLength={3} /></Field>
          <Field label="City"><Input value={form.fromCity} onChange={(e) => setForm({ ...form, fromCity: e.target.value })} placeholder="Mumbai" /></Field>
          <Field label="To"><Input value={form.toCode} onChange={(e) => setForm({ ...form, toCode: e.target.value })} placeholder="DEL" maxLength={3} /></Field>
          <Field label="City"><Input value={form.toCity} onChange={(e) => setForm({ ...form, toCity: e.target.value })} placeholder="New Delhi" /></Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
          <Field label="Departure"><Input type="datetime-local" value={form.departureAt} onChange={(e) => setForm({ ...form, departureAt: e.target.value })} /></Field>
          <Field label="Arrival"><Input type="datetime-local" value={form.arrivalAt} onChange={(e) => setForm({ ...form, arrivalAt: e.target.value })} /></Field>
        </div>
      </div>
    </Modal>
  );
}
